import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useWebSocketStore } from '../zustand-store/useWebSocketStore';
import { useGlobalStore } from '../zustand-store/useGlobalStore';
import { useGenerateAudioAsset } from '../http-hooks/audio-assets/useAudioAssets';
import { addMixPanelEvent } from '../store/actions';
import { ERROR_OCCURRED } from '../constants/mixpanel-events';

type AssetType = 'sound-effect' | 'music';

interface AudioAssetSettings {
  // Sound effect settings
  duration?: string | number;
  // Music settings
  instrumental?: boolean;
}

interface AudioAssetResult {
  audio_url: string;
  duration: number;
  prompt: string;
  fileSize: number;
  tags?: string;
  type?: AssetType;
}

interface UseAudioAssetGenerationReturn {
  isGenerating: boolean;
  generateAudioAsset: (prompt: string, settings: AudioAssetSettings) => Promise<void>;
  lastGeneratedAsset: AudioAssetResult | null;
}

/**
 * Custom hook for generating audio assets (sound effects & music) with WebSocket support
 * Unified hook that handles both types based on the assetType parameter
 */
export const useAudioAssetGeneration = (
  org_id: string,
  integration_id: string,
  assetType: AssetType,
  onSuccess?: (result: AudioAssetResult) => void
): UseAudioAssetGenerationReturn => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastGeneratedAsset, setLastGeneratedAsset] = useState<AudioAssetResult | null>(null);
  const dispatch = useDispatch();
  const requestQualityRating = useGlobalStore((state) => state.requestQualityRating);

  // Use refs to avoid stale closures
  const onSuccessRef = useRef(onSuccess);
  const isGeneratingRef = useRef(isGenerating);
  const assetTypeRef = useRef(assetType);
  
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    isGeneratingRef.current = isGenerating;
    assetTypeRef.current = assetType;
  });
  
  // WebSocket store methods
  const connectWebSocket = useWebSocketStore((state) => state.connect);
  const getConnectionId = useWebSocketStore((state) => state.getConnectionId);
  const setBuildProgressHandler = useWebSocketStore((state) => state.setBuildProgressHandler);
  const isConnectionHealthy = useWebSocketStore((state) => state.isConnectionHealthy);
  
  // React Query mutation for API call
  const generateMutation = useGenerateAudioAsset();

  const typeName = assetType === 'music' ? 'Music' : 'Sound effect';

  // Set up WebSocket message handler (stable reference)
  useEffect(() => {
    console.log(`🎵 Setting up ${assetTypeRef.current} WebSocket handler`);

    // Use buildProgressHandler for audio assets (similar to full audio build)
    const handler = (message: any) => {
      console.log('📨 Received audio asset message:', message);

      // Ignore if not generating
      if (!isGeneratingRef.current) {
        console.log('⚠️ Ignoring message, not generating');
        return;
      }

      // Handle error messages
      if (message.error || message.type === 'audio_generation_error') {
        setIsGenerating(false);
        const errorTypeName = assetTypeRef.current === 'music' ? 'music' : 'sound effect';
        toast.error(message.error || message.message || `Failed to generate ${errorTypeName}`);
        dispatch(addMixPanelEvent({
          orgId: org_id, integrationId: integration_id, body: {
            eventName: ERROR_OCCURRED,
            eventData: {
              error_type: 'generation_error',
              feature_name: assetTypeRef.current === 'music' ? 'music' : 'sound_effect',
              page: window.location.pathname,
            },
          },
        }));
        return;
      }

      // Handle completion messages (has data.audio_url)
      if (message.data && message.data.audio_url) {
        const result: AudioAssetResult = {
          audio_url: message.data.audio_url,
          duration: message.data.duration,
          prompt: message.data.prompt,
          fileSize: message.data.fileSize,
          tags: message.data.tags,
          type: message.data.type || assetTypeRef.current
        };
        
        setLastGeneratedAsset(result);
        setIsGenerating(false);
        
        const successTypeName = assetTypeRef.current === 'music' ? 'Music' : 'Sound effect';
        toast.success(`${successTypeName} generated successfully!`);
        
        requestQualityRating({
          tool: assetTypeRef.current === 'music' ? 'music' : 'sound_effect',
          source: assetTypeRef.current === 'music' ? 'music' : 'sound_effect',
          meta: { audioAssetId: message.data?.id, duration_sec: result.duration },
        });

        // Call success callback if provided
        if (onSuccessRef.current) {
          onSuccessRef.current(result);
        }
        return;
      }

      // Fallback: Handle direct audio_url (in case format changes)
      if (message.audio_url) {
        const result: AudioAssetResult = {
          audio_url: message.audio_url,
          duration: message.duration,
          prompt: message.prompt,
          fileSize: message.fileSize,
          tags: message.tags,
          type: message.type || assetTypeRef.current
        };
        
        setLastGeneratedAsset(result);
        setIsGenerating(false);
        
        const successTypeName = assetTypeRef.current === 'music' ? 'Music' : 'Sound effect';
        toast.success(`${successTypeName} generated successfully!`);
        
        requestQualityRating({
          tool: assetTypeRef.current === 'music' ? 'music' : 'sound_effect',
          source: assetTypeRef.current === 'music' ? 'music' : 'sound_effect',
          meta: { audioAssetId: message?.id, duration_sec: result.duration },
        });

        // Call success callback if provided
        if (onSuccessRef.current) {
          onSuccessRef.current(result);
        }
      }
    };

    setBuildProgressHandler(handler);

    return () => {
      console.log('🧹 Cleaning up audio asset WebSocket handler');
      setBuildProgressHandler(null);
    };
  }, [setBuildProgressHandler]);

  // Generate audio asset with WebSocket support
  const generateAudioAsset = useCallback(
    async (prompt: string, settings: AudioAssetSettings) => {
      const minLength = assetType === 'music' ? 10 : 10;
      if (!prompt || prompt.trim().length < minLength) {
        toast.error(`Please enter at least ${minLength} characters for the description`);
        return;
      }

      if (isGenerating) {
        toast.warning('Please wait for the current generation to complete');
        return;
      }

      try {
        setIsGenerating(true);

        // Ensure WebSocket is connected and get connection ID
        if (!isConnectionHealthy()) {
          console.log('⚠️ WebSocket not healthy, connecting...');
          connectWebSocket();
        }

        const socketConnectionId = await getConnectionId();
        
        if (!socketConnectionId) {
          toast.error('Connection issue. Please try again.');
          setIsGenerating(false);
          return;
        }

        console.log(`🎵 Starting ${assetType} generation with connectionId:`, socketConnectionId);

        // Call API with socket connection ID
        if (assetType === 'music') {
          // Music generation
          const duration = settings.duration === 'auto' ? 60 : parseInt(settings.duration?.toString() || '60');
          
          await generateMutation.mutateAsync({
            org_id,
            integration_id,
            type: 'music',
            prompt,
            socketConnectionId,
            duration,
            instrumental: settings.instrumental || false
          });
        } else {
          // Sound effect generation
          const seconds_total = settings.duration === 'auto' ? 30 : parseInt(settings.duration?.toString() || '30');
          
          await generateMutation.mutateAsync({
            org_id,
            integration_id,
            type: 'sound-effect',
            prompt,
            socketConnectionId,
            seconds_total
          });
        }

        console.log(`✅ ${typeName} generation initiated (async)`);
        // Keep loading state - will be handled by WebSocket

      } catch (error: any) {
        console.error(`❌ Failed to generate ${assetType}:`, error);
        setIsGenerating(false);
        toast.error(error.message || `Failed to generate ${typeName.toLowerCase()}. Please try again.`);
        dispatch(addMixPanelEvent({
          orgId: org_id, integrationId: integration_id, body: {
            eventName: ERROR_OCCURRED,
            eventData: {
              error_type: 'request_failed',
              feature_name: assetType === 'music' ? 'music' : 'sound_effect',
              page: window.location.pathname,
            },
          },
        }));
      }
    },
    [
      isGenerating,
      assetType,
      typeName,
      org_id,
      integration_id,
      generateMutation,
      getConnectionId,
      connectWebSocket,
      isConnectionHealthy,
      dispatch
    ]
  );

  return {
    isGenerating,
    generateAudioAsset,
    lastGeneratedAsset
  };
};
